import React from 'react';
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { APP_TIMEZONE } from '../config';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const URL_REGEX = /(https?:\/\/[^\s<>"'`]+|www\.[^\s<>"'`]+)/gi;
// Punctuation commonly typed right after a pasted URL (sentence-ending period,
// trailing comma, closing bracket, etc.) that isn't actually part of the link.
const TRAILING_PUNCTUATION_REGEX = /[)\]}>,.;:!?'"]+$/;

// Renders plain text with any http(s):// or www. URLs converted into clickable
// links, for display inside a whitespace-pre-wrap element. Returns an array of
// strings and <a> elements (safe to render directly as JSX children).
export function linkifyText(text) {
  if (!text) return text;

  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    const start = match.index;
    let url = match[0];

    const trailingMatch = url.match(TRAILING_PUNCTUATION_REGEX);
    const trailing = trailingMatch ? trailingMatch[0] : '';
    if (trailing) {
      url = url.slice(0, url.length - trailing.length);
    }

    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }

    const href = url.startsWith('www.') ? `https://${url}` : url;
    parts.push(
      React.createElement('a', {
        key: `link-${key++}`,
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
        className: 'underline break-all hover:opacity-80'
      }, url)
    );

    lastIndex = start + url.length;
    if (trailing) {
      parts.push(trailing);
    }
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

export function getApiBaseUrl() {
  const currentOrigin = window.location.origin;
  return currentOrigin;
}

function tzParts(date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIMEZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(new Date(date));
}

function getPart(parts, type) {
  const val = parts.find(p => p.type === type)?.value ?? '00';
  return type === 'hour' && val === '24' ? '00' : val;
}

export function toVancouverDateString(date) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIMEZONE }).format(new Date(date));
}

export function toDatetimeLocalVancouver(date) {
  const parts = tzParts(date);
  const get = (t) => getPart(parts, t);
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

// Offset (localWallClock - utc), in ms, that APP_TIMEZONE applies at the given
// UTC instant, derived from the IANA timezone database via Intl.
function tzOffsetMs(utcMs) {
  const parts = tzParts(new Date(utcMs));
  const get = (t) => getPart(parts, t);
  const localMs = new Date(
    `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:00Z`
  ).getTime();
  return localMs - utcMs;
}

export function fromDatetimeLocalVancouver(datetimeLocal) {
  // Solve utc = wall - offset(utc). The offset depends on the instant, so we
  // evaluate it twice: the second pass corrects wall times on DST transition
  // days, where the seed instant can land on the wrong side of the change.
  const wallMs = new Date(datetimeLocal + ':00Z').getTime();
  let utcMs = wallMs - tzOffsetMs(wallMs);
  utcMs = wallMs - tzOffsetMs(utcMs);
  return new Date(utcMs);
}


// Short relative timestamp ("Just now", "5m ago", "3d ago"), falling back to an
// absolute date past a week.
export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    timeZone: APP_TIMEZONE
  });
}
